import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "@/shared/lib/supabase";
import { queryKeys } from "@/shared/utils/queryKeys";
import type { GetNotificationsResponse } from "@/shared/types/notificationTypes";

async function fetchNotifications(estateId: number): Promise<GetNotificationsResponse> {
  const { data, error } = await supabase.functions.invoke("communications/notifications", {
    body: { estate_id: estateId },
    method: "POST",
  });
  if (error) throw new Error(error.message);
  return data as GetNotificationsResponse;
}

export function useNotifications(estateId: number | undefined, userId: number | undefined) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.user.notifications(estateId!),
    queryFn: () => fetchNotifications(estateId!),
    enabled: !!estateId,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!userId || !estateId) return;

    const channel = supabase
      .channel(`notifications-${userId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "estate_notifications",
          filter: `user_id=eq.${userId}`,
        },
        () => {
          queryClient.invalidateQueries({
            queryKey: queryKeys.user.notifications(estateId),
          });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, estateId, queryClient]);

  return query;
}
